import { Prose } from '../../components/Prose'
import { useEffect, useMemo, useState } from 'react'
import type { Me } from '../../api/client'
import { useDeleteWeight, useSaveWeight, useTodayLog, useSaveLog, useWeights } from '../../api/hooks'
import {
  computeMetrics, computeMilestones, computePhase, hasBabyPhase, offTarget,
} from '../../lib/metrics'
import { fmt, todayIso, trDate } from '../../lib/format'
import { WeightChart } from './WeightChart'
import { checks as buildChecks } from '../content/data'
import { TodayCard } from './TodayCard'
import { PlannedSport } from './PlannedSport'
import { BodyCard } from './BodyCard'
import { EnergyCard } from './EnergyCard'

/**
 * Takip sekmesi tek bir uzun sayfaydı: telefonda 7 ekran, 11 kart. Kullanıcı
 * "bugün ne yapmam lazım" sorusunun cevabını görmek için tartı tablosunu ve
 * kilometre taşlarını geçmek zorundaydı.
 *
 * Kartları kaldırmak yerine BÖLDÜK. Varsayılan bölüm "Bugün" ve iki kart:
 * günün planı ve yaptım işareti. Gerisi bir dokunuş uzakta ama gözden uzak.
 *
 * Bölüm seçimi URL'ye yazılmıyor — bilinçli. Sekme (#takip) kalıcı olmalı ama
 * içindeki bölüm oturumluk bir tercih; tarayıcı geri tuşunu bölüm geçişleriyle
 * doldurmak "geri"yi işe yaramaz hale getirirdi.
 */
const SECTIONS = [
  { id: 'bugun', label: 'Bugün' },
  { id: 'vucut', label: 'Vücut' },
  { id: 'enerji', label: 'Enerji' },
  { id: 'liste', label: 'Liste' },
] as const
type SectionId = (typeof SECTIONS)[number]['id']

export function TrackingTab({ me }: { me: Me }) {
  const [section, setSection] = useState<SectionId>('bugun')
  const weights = useWeights()
  const saveWeight = useSaveWeight()
  const delWeight = useDeleteWeight()
  const log = useTodayLog()
  const saveLog = useSaveLog()

  const [date, setDate] = useState(todayIso())
  const [kg, setKg] = useState('')

  const p = me.profile
  const rows = weights.data ?? []
  const m = computeMetrics(p, rows)
  const phase = computePhase(p, m)
  const milestones = computeMilestones(p, m)

  // Checkbox'lar yerel state'te tutuluyor: tık anında tepki versin.
  // Query'nin optimistic update'i bir mikrotask geç geliyor ve o gecikme
  // kontrollü input'ta "tıkladım ama olmadı" hissi yaratıyor.
  // Liste artık profilden üretiliyor (öğün saatleri kullanıcının kendi saatleri).
  const CHECKS = useMemo(() => buildChecks(p), [p.meal1, p.meal2])
  const [checks, setChecks] = useState<boolean[]>(() => Array(CHECKS.length).fill(false))
  const serverChecks = log.data?.checks
  useEffect(() => {
    if (serverChecks?.length === CHECKS.length) setChecks(serverChecks)
  }, [serverChecks])

  const doneCount = checks.filter(Boolean).length

  const toggleCheck = (i: number, v: boolean) => {
    const next = [...checks]
    next[i] = v
    setChecks(next)
    saveLog.mutate({
      checks: next,
      cycles: log.data?.cycles ?? 0,
      standSeconds: log.data?.standSeconds ?? 0,
      strengthDone: log.data?.strengthDone ?? false,
      updatedAt: new Date().toISOString(),
    })
  }

  const add = () => {
    const v = parseFloat(kg.replace(',', '.'))
    if (!date || !v) return
    saveWeight.mutate({ date, kg: v })
    setKg('')
  }

  const firstMs = rows.length ? new Date(rows[0].date).getTime() : Date.now()

  return (
    <>
      <div className="segbar" role="tablist" aria-label="Takip bölümleri">
        {SECTIONS.map((x) => (
          <button key={x.id} role="tab" aria-selected={section === x.id}
                  onClick={() => setSection(x.id)}>
            {x.label}
          </button>
        ))}
      </div>

      {section === 'bugun' && <>
      <TodayCard profile={p} />
      <PlannedSport profile={p} weightKg={m.now} />

      {/* Faz kartı doğum tarihine dayanıyor — girilmemişse gösterecek bir şey yok. */}
      {hasBabyPhase(p) && (
        <div className="card" style={{ borderLeft: '3px solid var(--s1)' }}>
          <div className="lab" style={{ fontSize: 12, color: 'var(--muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            {phase.name}
          </div>
          <h2 style={{ marginTop: 4 }}>{phase.title}</h2>
          <p style={{ color: 'var(--ink-2)', margin: '6px 0 0', fontSize: 14 }}>{phase.body}</p>
        </div>
      )}

      <div className="grid g4" style={{ marginBottom: 16 }}>
        <div className="tile">
          <div className="lab">Şu anki kilo</div>
          <div className="hero">{fmt(m.now)}<span className="hero-u">kg</span></div>
          <div className="dl">
            {rows.length && m.lost > 0.05
              ? <>Başlangıçtan <strong style={{ color: 'var(--good-text)' }}>−{fmt(m.lost)} kg</strong> · VKİ {fmt(m.bmi)}</>
              : <>Henüz kayıt yok · VKİ {fmt(m.bmi)}</>}
          </div>
        </div>
        {/* Kalori ve protein burada DEĞİL: aşağıdaki Enerji kartında, hata payı
            ve o günün aktiviteleriyle birlikte. Aynı sayıyı iki yerde iki farklı
            hesapla göstermek, hangisinin doğru olduğunu soran bir kullanıcı üretir. */}
        <Tile label="Haftalık hedef hız" value={`${fmt(p.weeklyRateKg)} kg`}
              detail={`Vücut ağırlığının %${((p.weeklyRateKg / m.now) * 100).toFixed(2)}'i · güvenli bant %0.5–0.75`} />
        <Tile label="Hedefe kalan" value={`${fmt(m.remaining)} kg`}
              detail={m.weeksToTarget > 0
                ? `Bu hızda ≈ ${Math.round(m.weeksToTarget)} hafta (${(m.weeksToTarget / 4.345).toFixed(1)} ay)`
                : 'Hedefe ulaştın'} />
      </div>
      </>}

      {section === 'vucut' && <>
      <BodyCard profile={p} weightKg={m.now} />

      <div className="card chart-card">
        <h2>Kilo seyri</h2>
        <Prose className="card-h">Haftada bir, aynı gün ve aynı saatte tartıl.</Prose>
        <WeightChart profile={p} weights={rows} />
        <Prose className="note">Günlük dalgalanma (tuz, su, bağırsak) 1–2 kg olabilir — tek bir tartıya bakma, çizginin yönüne bak.</Prose>
      </div>

      <div className="card">
        <h2>Tartı kaydı</h2>
        <div className="row" style={{ marginBottom: 14 }}>
          <div><label className="f">Tarih</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div><label className="f">Kilo (kg)</label>
            {/* Yer tutucu nötr: eskiden "115.0" yazıyordu — yani geliştiricinin
                kilosu. Örnek sayı bile hesabın kime ait olduğunu ele veriyor. */}
            <input type="number" step="0.1" value={kg} placeholder="örn. 78.5"
                   onChange={(e) => setKg(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
          </div>
          <button className="primary" onClick={add} disabled={!kg}>Ekle</button>
        </div>
        <table>
          <thead><tr><th>Tarih</th><th>Kilo</th><th>Değişim</th><th>Hedefe göre</th><th /></tr></thead>
          <tbody>
            {!rows.length && <tr><td colSpan={5} style={{ color: 'var(--muted)' }}>Henüz kayıt yok — ilk tartını ekle.</td></tr>}
            {[...rows].reverse().map((r, i, arr) => {
              const prev = arr[i + 1]
              const dv = prev ? r.kg - prev.kg : null
              const off = offTarget(p, firstMs, r)
              return (
                <tr key={r.date}>
                  <td>{trDate(r.date)}</td>
                  <td><strong>{fmt(r.kg)}</strong></td>
                  <td>{dv === null
                    ? <span style={{ color: 'var(--muted)' }}>—</span>
                    : <span style={{ color: dv <= 0 ? 'var(--good-text)' : 'var(--crit)' }}>
                        {dv > 0 ? '+' : '−'}{fmt(Math.abs(dv))}
                      </span>}
                  </td>
                  <td><span style={{ color: off <= 0.3 ? 'var(--good-text)' : 'var(--ink-2)' }}>
                    {off <= 0 ? 'önde ' : 'geride '}{fmt(Math.abs(off))} kg
                  </span></td>
                  <td className="act"><button onClick={() => delWeight.mutate(r.date)}>sil</button></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      </>}

      {section === 'enerji' && <EnergyCard profile={p} weights={rows} weightKg={m.now} />}

      {section === 'liste' && <>
      <div className="card">
        <h2>Bugünün listesi</h2>
        <Prose className="card-h">Hepsini değil, çoğunu tuttur. Haftanın 5 gününde bu liste dolarsa kilo zaten iner.</Prose>
        {CHECKS.map((c, i) => (
          <label className="chk" key={c[0]}>
            <input type="checkbox" checked={!!checks[i]} onChange={(e) => toggleCheck(i, e.target.checked)} />
            <span><span className="t">{c[0]}</span><br /><span className="d">{c[1]}</span></span>
          </label>
        ))}
        <div className="bar-track"><div className="bar-fill" style={{ width: `${(doneCount / CHECKS.length) * 100}%` }} /></div>
        <Prose className="note">{doneCount} / {CHECKS.length} tamam{doneCount >= 6 ? ' — iyi gün 👌' : ''}</Prose>
      </div>

      <div className="card">
        <h2>Kilometre taşları</h2>
        <Prose className="card-h">Hedefi tek parça düşünmek bunaltıcı. Sırayla şu duraklara bak.</Prose>
        <div className="grid g4">
          {milestones.map((x) => (
            <div className="tile" key={x.label}>
              <div className="lab">{x.label}{x.done && <> · <span style={{ color: 'var(--good-text)' }}>tamam ✓</span></>}</div>
              <div className="val">{fmt(x.kg, 0)} kg</div>
              <div className="dl">{x.done ? x.why : <><strong>{fmt(x.remaining)} kg</strong> kaldı · {x.why}</>}</div>
            </div>
          ))}
        </div>
      </div>
      </>}
    </>
  )
}

function Tile({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="tile">
      <div className="lab">{label}</div>
      <div className="val">{value}</div>
      <div className="dl">{detail}</div>
    </div>
  )
}
